import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import apiClient from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { RoleIncentiveLedger } from './RoleIncentiveLedger';
import { CounsellorIncentives } from './personal-incentives/CounsellorIncentives';

export const PersonalIncentivesEntry = () => {
  const { user } = useAuth();
  return ['counselor', 'team_leader'].includes(user?.role) ? <CounsellorIncentives /> : <RoleIncentiveLedger />;
};

export default function IncentiveDashboard() {
  const { user } = useAuth();
  if (['counselor', 'team_leader'].includes(user?.role)) return <CounsellorIncentives />;
  return ['admin', 'operating_head'].includes(user?.role)
    ? <ManagementIncentiveDashboard />
    : <div className="p-3 sm:p-6" data-testid="personal-incentives-page"><RoleIncentiveLedger /></div>;
}

function ManagementIncentiveDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Data states
  const [leaderboard, setLeaderboard] = useState(null);
  const [weeklyBonus, setWeeklyBonus] = useState(null);
  const [monthlyChampion, setMonthlyChampion] = useState(null);
  const [firstStrike, setFirstStrike] = useState(null); // deprecated (First Strike removed)
  const [teamBonus, setTeamBonus] = useState(null);
  const [intakePeriods, setIntakePeriods] = useState([]);
  const [selectedIntake, setSelectedIntake] = useState('');
  const [intakeChampion, setIntakeChampion] = useState(null);
  const [powerHoursStatus, setPowerHoursStatus] = useState(null);
  const [settings, setSettings] = useState(null);

  // Month selector — review past months' leaderboard & champion
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const monthOptions = React.useMemo(() => {
    const opts = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
      const value = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const label = dt.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
      opts.push({ value: i === 0 ? '' : value, label: i === 0 ? `${label} (Current)` : label });
    }
    return opts;
  }, []);
  
  // History data for past months
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Fetch all data
  const fetchAllData = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      // Fetch each endpoint individually with error handling
      const results = await Promise.allSettled([
        apiClient.get(`/incentive/leaderboard?${new URLSearchParams({ ...(selectedMonth ? { month: selectedMonth } : {}), ...(selectedCountry ? { country: selectedCountry } : {}) })}`),
        apiClient.get(`/incentive/weekly-bonus${selectedMonth ? `?month=${selectedMonth}` : ''}`),
        apiClient.get(`/incentive/monthly-champion${selectedMonth ? `?month=${selectedMonth}` : ''}`),
        apiClient.get('/power-hours/status'),
        apiClient.get('/incentive/settings'),
        apiClient.get(`/incentive/team-bonus${selectedMonth ? `?month=${selectedMonth}` : ''}`),
        apiClient.get('/system-settings/intakes')
      ]);
      
      // Process results - handle both fulfilled and rejected
      const [leaderboardRes, weeklyRes, monthlyRes, powerHoursRes, settingsRes, teamBonusRes, intakesRes] = results;
      
      // Set state for each result, checking if fulfilled
      setLeaderboard(leaderboardRes.status === 'fulfilled' ? leaderboardRes.value.data : null);
      setWeeklyBonus(weeklyRes.status === 'fulfilled' ? weeklyRes.value.data : null);
      setMonthlyChampion(monthlyRes.status === 'fulfilled' ? monthlyRes.value.data : null);
      setPowerHoursStatus(powerHoursRes.status === 'fulfilled' ? powerHoursRes.value.data : null);
      setSettings(settingsRes.status === 'fulfilled' ? settingsRes.value.data : null);
      setTeamBonus(teamBonusRes.status === 'fulfilled' ? teamBonusRes.value.data : null);
      setIntakePeriods(intakesRes.status === 'fulfilled' ? (intakesRes.value.data?.periods || []) : []);
      
      // Log any failed requests for debugging
      const failedRequests = results
        .map((r, i) => ({ result: r, endpoint: ['leaderboard', 'weekly-bonus', 'monthly-champion', 'power-hours', 'settings', 'team-bonus', 'intakes'][i] }))
        .filter(r => r.result.status === 'rejected');
      
      if (failedRequests.length > 0) {
        console.warn('Some incentive endpoints failed:', failedRequests.map(f => `${f.endpoint}: ${f.result.reason?.message || 'Unknown error'}`));
        // Only show error if critical endpoints failed
        if (leaderboardRes.status === 'rejected' || weeklyRes.status === 'rejected') {
          toast.error('Some incentive data failed to load. Please refresh.');
        }
      }
      
      // Pool/Power Hours leads are shown in Leads module only
    } catch (error) {
      console.error('Error fetching incentive data:', error);
      if (!silent) toast.error('Failed to load incentive dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, selectedMonth, selectedCountry]);

  // Fetch intake champion data when intake is selected
  const fetchIntakeChampion = useCallback(async (intakeId) => {
    if (!intakeId) {
      setIntakeChampion(null);
      return;
    }
    try {
      const response = await apiClient.get(`/incentive/intake-champion/${intakeId}`);
      setIntakeChampion(response.data);
    } catch (error) {
      console.error('Error fetching intake champion:', error);
      setIntakeChampion(null);
    }
  }, []);

  useEffect(() => {
    if (selectedIntake) {
      fetchIntakeChampion(selectedIntake);
    }
  }, [selectedIntake, fetchIntakeChampion]);

  useEffect(() => {
    fetchAllData();
    // Soft refresh every 30 seconds — keeps the data on screen and only swaps
    // values in once the silent fetch returns, so the page never blanks.
    const interval = setInterval(() => fetchAllData({ silent: true }), 30000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  // Fetch history data when switching to history tab
  const fetchHistoryData = async () => {
    if (historyData.length > 0) return; // Already fetched
    setLoadingHistory(true);
    try {
      const response = await apiClient.get('/incentive/history?months=12');
      setHistoryData(response.data.history || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load history data');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch history when tab changes to history
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistoryData();
    }
  }, [activeTab]);

  // Get user's rank in leaderboard
  const getUserRank = () => {
    if (!leaderboard?.leaderboard || !user) return null;
    const index = leaderboard.leaderboard.findIndex(c => c.counselor_id === user.id);
    return index >= 0 ? index + 1 : null;
  };

  // Get user's stats
  const getUserStats = () => {
    if (!leaderboard?.leaderboard || !user) return null;
    return leaderboard.leaderboard.find(c => c.counselor_id === user.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <i className="fas fa-trophy text-4xl text-yellow-500 mb-4 animate-pulse"></i>
          <p className="text-gray-500">Loading incentive dashboard...</p>
        </div>
      </div>
    );
  }

  const userRank = getUserRank();
  const userStats = getUserStats();

  return (
    <div className="space-y-4 p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <i className="fas fa-trophy text-yellow-500"></i>
            Incentive Dashboard
          </h1>
          <p className="text-xs sm:text-sm text-gray-500">
            {leaderboard?.month} | {leaderboard?.week}
            {refreshing && (
              <span className="ml-2 inline-flex items-center text-indigo-600">
                <i className="fas fa-sync-alt fa-spin mr-1 text-[10px]"></i>
                <span className="text-[11px]">refreshing…</span>
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="h-10 flex-1 min-w-0 sm:flex-initial sm:w-auto rounded-md border border-gray-300 bg-white px-2 text-xs sm:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            data-testid="incentive-month-selector"
            title="Review a past month's leaderboard & champion"
          >
            {monthOptions.map((o) => (
              <option key={o.value || 'current'} value={o.value}>{o.label}</option>
            ))}
          </select>
          <Button onClick={() => fetchAllData({ silent: true })} variant="outline" size="sm" disabled={refreshing} className="h-10 shrink-0">
            <i className={`fas fa-sync-alt sm:mr-2 ${refreshing ? 'fa-spin' : ''}`}></i>
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {leaderboard?.is_past_month && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="past-month-banner">
          <i className="fas fa-clock-rotate-left mr-1"></i>
          Viewing <b>{leaderboard.month}</b> (past month). Monthly admissions & champion reflect that month; weekly bonus applies to the current month only.
        </div>
      )}

      {/* Power Hours Banner */}
      {/* Power Hours Banner */}
      {powerHoursStatus?.is_active && (
        <Card className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <i className="fas fa-bolt text-3xl animate-pulse"></i>
                <div>
                  <h3 className="font-bold text-lg">Power Hours Active!</h3>
                  <p className="text-sm opacity-90">
                    {powerHoursStatus.start_time} - {powerHoursStatus.end_time} IST | 
                    {powerHoursStatus.bonus_percentage}% Extra Bonus
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Stats Card - For Counselors */}
      {user?.role === 'counselor' && userStats && (
        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <i className="fas fa-user-circle text-indigo-600"></i>
              My Performance
              {userRank && (
                <Badge variant={userRank <= 3 ? "default" : "secondary"} className="ml-2">
                  Rank #{userRank}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-indigo-600">{userStats.monthly_admissions}</p>
                <p className="text-xs text-gray-500">Monthly Admissions</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-green-600">{userStats.weekly_admissions}</p>
                <p className="text-xs text-gray-500">This Week</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-amber-600">₹{userStats.base_earnings?.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Base Earnings</p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-purple-600">
                  {userStats.weekly_eligible ? (
                    <span className="flex items-center justify-center gap-1">
                      <i className="fas fa-check-circle text-green-500"></i>
                      ₹{userStats.weekly_bonus?.toLocaleString()}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </p>
                <p className="text-xs text-gray-500">Weekly Bonus</p>
              </div>
            </div>
            
            {/* Weekly Progress */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Weekly Target Progress</span>
                <span className="text-xs font-medium">
                  {userStats.weekly_admissions}/{settings?.weekly_min_admissions || 3}
                </span>
              </div>
              <Progress 
                value={Math.min(100, (userStats.weekly_admissions / (settings?.weekly_min_admissions || 3)) * 100)} 
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full h-auto gap-1.5 p-1.5 bg-slate-100 rounded-xl">
          <TabsTrigger value="basic" className="min-h-[44px] px-1 rounded-lg border border-transparent text-[11px] sm:text-xs font-medium text-slate-600 transition-all data-[state=active]:border-teal-200 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm" data-testid="incentive-basic-tab">
            <span className="truncate">Basic</span>
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="min-h-[44px] px-1 rounded-lg border border-transparent text-[11px] sm:text-xs font-medium text-slate-600 transition-all data-[state=active]:border-teal-200 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm">
            <i className="fas fa-list-ol mr-1 hidden sm:inline"></i>
            <span className="truncate"><span className="hidden sm:inline">Leaderboard</span><span className="sm:hidden">Rank</span></span>
          </TabsTrigger>
          <TabsTrigger value="weekly" className="min-h-[44px] px-1 rounded-lg border border-transparent text-[11px] sm:text-xs font-medium text-slate-600 transition-all data-[state=active]:border-teal-200 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm">
            <i className="fas fa-calendar-week mr-1 hidden sm:inline"></i>
            <span className="truncate"><span className="hidden sm:inline">Weekly</span><span className="sm:hidden">Week</span></span>
          </TabsTrigger>
          <TabsTrigger value="team" className="min-h-[44px] px-1 rounded-lg border border-transparent text-[11px] sm:text-xs font-medium text-slate-600 transition-all data-[state=active]:border-teal-200 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm">
            <i className="fas fa-users mr-1 hidden sm:inline"></i>
            <span className="truncate"><span className="hidden sm:inline">Team/Country</span><span className="sm:hidden">Team</span></span>
          </TabsTrigger>
          <TabsTrigger value="intake" className="min-h-[44px] px-1 rounded-lg border border-transparent text-[11px] sm:text-xs font-medium text-slate-600 transition-all data-[state=active]:border-teal-200 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm">
            <i className="fas fa-award mr-1 hidden sm:inline"></i>
            <span className="truncate">Intake</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="min-h-[44px] px-1 rounded-lg border border-transparent text-[11px] sm:text-xs font-medium text-slate-600 transition-all data-[state=active]:border-teal-200 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm">
            <i className="fas fa-history mr-1 hidden sm:inline"></i>
            <span className="truncate"><span className="hidden sm:inline">History</span><span className="sm:hidden">Hist</span></span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic"><RoleIncentiveLedger /></TabsContent>
        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard">
          <Card>
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <i className="fas fa-trophy text-yellow-500"></i>
                  Monthly Leaderboard - {leaderboard?.month || 'Loading...'}
                </CardTitle>
                <CardDescription>
                  Rankings based on successful admissions
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {/* Monthly Champion — merged from the old Champions tab */}
              {monthlyChampion && (
                <div className="mb-3 rounded-lg border border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50 p-3" data-testid="leaderboard-champion-banner">
                  {monthlyChampion.champion ? (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shrink-0">
                        <i className="fas fa-crown text-white"></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Monthly Champion · {monthlyChampion.month}</p>
                        <p className="text-sm font-bold text-gray-800 truncate">{monthlyChampion.champion.counselor_name} · {monthlyChampion.champion.admissions} admissions</p>
                        {monthlyChampion.champion.is_tie && (
                          <p className="text-[11px] text-orange-600 mt-0.5"><i className="fas fa-exclamation-triangle mr-1"></i>Tie detected — admin override may be needed</p>
                        )}
                      </div>
                      <Badge className="bg-yellow-500 text-white text-sm px-3 py-1 shrink-0">+₹{monthlyChampion.champion.bonus_amount?.toLocaleString()}</Badge>
                    </div>
                  ) : (
                    <p className="text-xs text-amber-700 flex items-center gap-2"><i className="fas fa-crown text-yellow-400"></i>No champion yet — min {monthlyChampion.min_admissions} admissions required for the ₹ champion bonus.</p>
                  )}
                  <p className="text-[10px] text-gray-500 mt-1.5">Champion bonus is auto-created in Incentives → Bonuses, where you mark it Eligible / Paid.</p>
                </div>
              )}
              {!leaderboard?.leaderboard?.length ? (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-users text-3xl mb-2"></i>
                  <p>No counselor data available</p>
                </div>
              ) : (
              <div className="space-y-2">
                {leaderboard?.leaderboard?.map((counselor, index) => (
                  <div 
                    key={counselor.counselor_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      index === 0 ? 'bg-gradient-to-r from-yellow-100 to-amber-100 border border-yellow-300' :
                      index === 1 ? 'bg-gradient-to-r from-gray-100 to-slate-100 border border-gray-300' :
                      index === 2 ? 'bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200' :
                      counselor.counselor_id === user?.id ? 'bg-indigo-50 border border-indigo-200' :
                      'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? 'bg-yellow-500 text-white' :
                        index === 1 ? 'bg-gray-400 text-white' :
                        index === 2 ? 'bg-orange-400 text-white' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          {counselor.counselor_name}
                          {counselor.counselor_id === user?.id && (
                            <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {counselor.monthly_admissions} admissions | ₹{counselor.base_earnings?.toLocaleString()} earned
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {counselor.weekly_eligible && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <i className="fas fa-check mr-1"></i>Weekly Bonus
                        </Badge>
                      )}
                      {counselor.champion_eligible && index === 0 && (
                        <Badge className="bg-yellow-500 text-white text-xs ml-1" data-testid="leaderboard-champion-badge">
                          <i className="fas fa-crown mr-1"></i>Champion
                          {monthlyChampion?.champion?.bonus_amount ? ` +₹${monthlyChampion.champion.bonus_amount.toLocaleString()}` : ''}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Bonus Tab */}
        <TabsContent value="weekly">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="fas fa-calendar-week text-blue-500"></i>
                Weekly Bonus Status - {weeklyBonus?.month || weeklyBonus?.week || 'Loading...'}
              </CardTitle>
              <CardDescription>
                Minimum {weeklyBonus?.min_admissions || '...'} admissions = ₹{weeklyBonus?.bonus_amount?.toLocaleString() || '...'} bonus
              </CardDescription>
            </CardHeader>
            <CardContent>
              {weeklyBonus?.weeks?.length > 0 ? (
                <div className="space-y-4">
                  {weeklyBonus.weeks.map((wk) => (
                    <div key={wk.week_number} className={`border rounded-lg overflow-hidden ${wk.is_current ? 'border-blue-500 ring-2 ring-blue-400 shadow-sm' : 'border-gray-200'}`} data-testid={`weekly-week-${wk.week_number}`}>
                      <div className={`flex items-center justify-between px-3 py-2 border-b ${wk.is_current ? 'bg-blue-600' : 'bg-blue-50'}`}>
                        <div className={`font-semibold text-sm ${wk.is_current ? 'text-white' : 'text-blue-900'}`}>
                          <i className="fas fa-calendar-week mr-2"></i>{wk.label}
                          <span className={`ml-2 text-xs font-normal ${wk.is_current ? 'text-blue-100' : 'text-blue-700'}`}>{wk.date_range}</span>
                          {wk.is_current && <Badge className="ml-2 bg-white text-blue-700 text-[10px] align-middle" data-testid={`weekly-week-${wk.week_number}-current`}>Current week</Badge>}
                        </div>
                        <Badge variant="outline" className={`text-[11px] ${wk.is_current ? 'border-white text-white' : 'border-green-300 text-green-700'}`}>
                          {wk.eligible_count} eligible
                        </Badge>
                      </div>
                      <div className="p-2 space-y-1.5">
                        {wk.counselors?.filter(c => c.admissions > 0 || c.eligible).length === 0 && (
                          <p className="text-xs text-gray-400 px-2 py-1">No admissions this week.</p>
                        )}
                        {wk.counselors?.filter(c => c.admissions > 0 || c.eligible).map((c) => (
                          <div key={c.counselor_id} className={`flex items-center justify-between px-2 py-1.5 rounded ${c.eligible ? 'bg-green-50' : 'bg-gray-50'}`}>
                            <span className="text-sm text-gray-800">{c.counselor_name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">{c.admissions}/{weeklyBonus?.min_admissions}</span>
                              {c.eligible && <Badge className="bg-green-600 text-[10px]">+₹{weeklyBonus?.bonus_amount?.toLocaleString()}</Badge>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <i className="fas fa-info-circle mr-2"></i>
                  <strong>{weeklyBonus?.eligible_count || 0}</strong> counselor(s) eligible for weekly bonus this week
                </p>
              </div>
              <div className="space-y-2">
                {weeklyBonus?.counselors?.map((counselor) => (
                  <div 
                    key={counselor.counselor_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      counselor.eligible ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        counselor.eligible ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {counselor.eligible ? (
                          <i className="fas fa-check"></i>
                        ) : (
                          <span className="text-xs font-bold">{counselor.admissions}</span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{counselor.counselor_name}</p>
                        <p className="text-xs text-gray-500">
                          {counselor.admissions} / {weeklyBonus?.min_admissions} admissions
                        </p>
                      </div>
                    </div>
                    <div>
                      {counselor.eligible ? (
                        <Badge className="bg-green-600">
                          +₹{weeklyBonus?.bonus_amount?.toLocaleString()}
                        </Badge>
                      ) : (
                        <span className="text-xs text-gray-500">
                          {counselor.shortfall} more needed
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Performance Tab */}
        <TabsContent value="team">
          <Card>
            <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <i className={`fas ${teamBonus?.team_enabled === false ? 'fa-globe text-emerald-500' : 'fa-users text-purple-500'}`}></i>
                    {teamBonus?.team_enabled === false ? 'Admissions by Country' : 'Team Performance Bonus'}
                  </CardTitle>
                  <CardDescription>
                    {teamBonus?.team_enabled === false
                      ? 'Team mode is OFF — showing individual admissions grouped by destination country'
                      : `Bonus when team exceeds target: (Members × ${settings?.team_target_multiplier || 5}) + ${settings?.team_bonus_extra_threshold || 5} admissions`}
                  </CardDescription>
                </div>
                {user?.role === 'admin' && (
                  <button
                    type="button"
                    data-testid="team-mode-toggle"
                    onClick={async () => {
                      try {
                        const next = !(teamBonus?.team_enabled);
                        await apiClient.put('/incentive/settings', { team_bonus_enabled: next });
                        toast.success(next ? 'Team mode ON' : 'Team mode OFF — showing country view');
                        fetchAllData({ silent: true });
                      } catch (e) { toast.error('Failed to update team mode'); }
                    }}
                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${teamBonus?.team_enabled === false ? 'bg-gray-100 text-gray-600 border-gray-300' : 'bg-purple-600 text-white border-purple-600'}`}
                  >
                    <i className={`fas ${teamBonus?.team_enabled === false ? 'fa-toggle-off' : 'fa-toggle-on'}`}></i>
                    Team {teamBonus?.team_enabled === false ? 'OFF' : 'ON'}
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {teamBonus?.team_enabled === false ? (
                (teamBonus?.country_breakdown?.length > 0 ? (
                  <div className="space-y-3" data-testid="country-breakdown">
                    {teamBonus.country_breakdown.map((c) => (
                      <div key={c.country} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 border-b">
                          <div className="font-semibold text-emerald-900 text-sm"><i className="fas fa-map-marker-alt mr-2"></i>{c.country}</div>
                          <Badge className="bg-emerald-600 text-white text-[11px]">{c.total} admission{c.total !== 1 ? 's' : ''}</Badge>
                        </div>
                        <div className="p-2 space-y-1">
                          {c.counselors.map((m) => (
                            <div key={m.counselor_name} className="flex items-center justify-between px-2 py-1 text-sm">
                              <span className="text-gray-700">{m.counselor_name}</span>
                              <span className="font-medium text-gray-900">{m.admissions}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500"><i className="fas fa-globe text-4xl text-gray-300 mb-3"></i><p>No admissions in this period</p></div>
                ))
              ) : (
              <>
              {teamBonus?.teams?.length > 0 ? (
                <div className="space-y-4">
                  {teamBonus.teams.map((team) => {
                    const target = (team.member_count * (settings?.team_target_multiplier || 5)) + (settings?.team_bonus_extra_threshold || 5);
                    const progress = Math.min(100, (team.total_admissions / target) * 100);
                    const qualified = team.total_admissions >= target;
                    
                    return (
                      <div 
                        key={team.team_id}
                        className={`p-4 rounded-lg border ${qualified ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${qualified ? 'bg-green-500' : 'bg-gray-400'}`}>
                              <i className="fas fa-users text-white"></i>
                            </div>
                            <div>
                              <p className="font-medium">{team.team_name || 'Team'}</p>
                              <p className="text-xs text-gray-500">{team.member_count} members</p>
                            </div>
                          </div>
                          {qualified ? (
                            <Badge className="bg-green-500">
                              <i className="fas fa-check mr-1"></i>
                              Qualified! +₹{((settings?.team_bonus_amount || 2000) * team.member_count).toLocaleString()}
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              {target - team.total_admissions} more needed
                            </Badge>
                          )}
                        </div>
                        
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>Progress: {team.total_admissions} / {target}</span>
                            <span className="font-medium">{Math.round(progress)}%</span>
                          </div>
                          <Progress value={progress} className={`h-2 ${qualified ? '[&>div]:bg-green-500' : ''}`} />
                        </div>
                        
                        {/* Team Members */}
                        {team.members?.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs font-medium text-gray-500 mb-2">Members:</p>
                            <div className="grid grid-cols-2 gap-2">
                              {team.members.map((member) => (
                                <div key={member.counselor_id} className="flex items-center justify-between text-sm p-2 bg-white rounded">
                                  <span className="truncate">{member.counselor_name}</span>
                                  <Badge variant={member.admissions >= (settings?.team_member_min_admissions || 5) ? "default" : "secondary"} className="text-xs">
                                    {member.admissions}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-users text-4xl text-gray-300 mb-3"></i>
                  <p>No team data available</p>
                  <p className="text-xs mt-2">Teams are configured in Admin Settings</p>
                </div>
              )}
              
              {/* Bonus Info */}
              <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-xs text-purple-700">
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>How it works:</strong> Each team member earns ₹{(settings?.team_bonus_amount || 2000).toLocaleString()} 
                  when the team reaches target. Each member must have at least {settings?.team_member_min_admissions || 5} admissions.
                </p>
              </div>
              </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Intake Champion Tab */}
        <TabsContent value="intake">
          <Card>
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
              <CardTitle className="flex items-center gap-2">
                <i className="fas fa-award text-emerald-500"></i>
                Intake Champion
              </CardTitle>
              <CardDescription>
                Grand prize: ₹{(settings?.intake_champion_bonus || 25000).toLocaleString()} for top performer
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              {/* Intake Period Selector */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Select Intake Period:</label>
                <Select value={selectedIntake || "none"} onValueChange={(v) => setSelectedIntake(v === "none" ? "" : v)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose an intake period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Select Intake --</SelectItem>
                    {intakePeriods.map((intake) => (
                      <SelectItem key={intake.id} value={intake.id}>
                        {intake.name} {intake.is_active && '(Active)'}
                        {intake.start_date && ` - ${intake.start_date}`}
                        {intake.end_date && ` to ${intake.end_date}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* No intake periods message */}
              {intakePeriods.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-calendar-plus text-4xl text-gray-300 mb-3"></i>
                  <p>No intake periods configured</p>
                  <p className="text-xs mt-2">Admin can add intake periods in Settings → Intakes</p>
                </div>
              )}

              {/* Select prompt */}
              {intakePeriods.length > 0 && !selectedIntake && (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-hand-pointer text-4xl text-gray-300 mb-3"></i>
                  <p>Select an intake period above to view rankings</p>
                  <p className="text-xs mt-2">Min {settings?.intake_champion_min_admissions || 25} flown required</p>
                </div>
              )}

              {/* Intake Champion Display */}
              {selectedIntake && intakeChampion && (
                <div>
                  {intakeChampion.champion ? (
                    <div className="text-center mb-6 p-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg">
                      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                        <i className="fas fa-trophy text-white text-3xl"></i>
                      </div>
                      <Badge className="bg-emerald-500 text-lg px-4 py-1 mb-2">
                        Intake Champion
                      </Badge>
                      <h3 className="text-xl font-bold text-gray-800">
                        {intakeChampion.champion.counselor_name}
                      </h3>
                      <p className="text-3xl font-bold text-emerald-600 my-1">
                        {intakeChampion.champion.flown} Flown
                      </p>
                      <p className="text-xs text-gray-600 mb-2">
                        {intakeChampion.champion.admissions} total admissions (active + flown)
                      </p>
                      <Badge className="bg-yellow-500 text-lg">
                        🏆 ₹{(settings?.intake_champion_bonus || 25000).toLocaleString()}
                      </Badge>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500 mb-4">
                      <i className="fas fa-trophy text-3xl text-gray-300 mb-2"></i>
                      <p>No champion yet for this intake</p>
                      <p className="text-xs">Min {settings?.intake_champion_min_admissions || 25} flown required</p>
                    </div>
                  )}

                  {/* Rankings */}
                  {intakeChampion.rankings?.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-gray-700 mb-3">
                        <i className="fas fa-list-ol mr-2"></i>
                        Intake Rankings:
                      </p>
                      <div className="space-y-2">
                        {intakeChampion.rankings.map((r, i) => (
                          <div 
                            key={r.counselor_id}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              i === 0 ? 'bg-gradient-to-r from-emerald-100 to-teal-100 border border-emerald-300' :
                              i === 1 ? 'bg-gray-100' :
                              i === 2 ? 'bg-orange-50' : 'bg-white border'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                                i === 0 ? 'bg-emerald-500 text-white' :
                                i === 1 ? 'bg-gray-400 text-white' :
                                i === 2 ? 'bg-orange-400 text-white' : 'bg-gray-200'
                              }`}>
                                {i + 1}
                              </span>
                              <span className="font-medium">{r.counselor_name}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-emerald-600">{r.flown} <span className="text-xs font-normal text-gray-500">flown</span></p>
                              <p className="text-xs text-gray-500">{r.admissions} admissions</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {intakeChampion.rankings?.length === 0 && (
                    <p className="text-center text-sm text-gray-500">
                      No admissions recorded for this intake period yet
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <i className="fas fa-history text-purple-500"></i>
                  Performance History
                </h3>
                <Badge variant="outline" className="text-xs">
                  Last 12 Months
                </Badge>
              </div>

              {loadingHistory ? (
                <div className="text-center py-8">
                  <i className="fas fa-spinner fa-spin text-2xl text-purple-500 mb-2"></i>
                  <p className="text-gray-500 text-sm">Loading history...</p>
                </div>
              ) : historyData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-calendar-times text-3xl mb-2"></i>
                  <p>No historical data available</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-2 font-medium text-gray-600">Month</th>
                        <th className="text-center py-2 px-2 font-medium text-gray-600">Admissions</th>
                        <th className="text-right py-2 px-2 font-medium text-gray-600">Base</th>
                        <th className="text-right py-2 px-2 font-medium text-gray-600">Weekly</th>
                        <th className="text-right py-2 px-2 font-medium text-gray-600">Monthly</th>
                        <th className="text-right py-2 px-2 font-medium text-gray-600">Team</th>
                        <th className="text-right py-2 px-2 font-medium text-gray-600 bg-green-50">Total</th>
                        <th className="text-right py-2 px-2 font-medium text-gray-600">Paid</th>
                        <th className="text-right py-2 px-2 font-medium text-gray-600">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.map((month, index) => (
                        <tr 
                          key={month.month} 
                          className={`border-b hover:bg-gray-50 ${month.is_current_month ? 'bg-blue-50' : ''}`}
                        >
                          <td className="py-2 px-2 font-medium">
                            {month.month}
                            {month.is_current_month && (
                              <Badge className="ml-1 text-[8px] bg-blue-500">Current</Badge>
                            )}
                          </td>
                          <td className="text-center py-2 px-2">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full font-bold">
                              {month.admissions}
                            </span>
                          </td>
                          <td className="text-right py-2 px-2 text-gray-600">₹{month.base_commission?.toLocaleString() || 0}</td>
                          <td className="text-right py-2 px-2 text-gray-600">₹{month.weekly_bonus?.toLocaleString() || 0}</td>
                          <td className="text-right py-2 px-2 text-gray-600">₹{month.monthly_bonus?.toLocaleString() || 0}</td>
                          <td className="text-right py-2 px-2 text-gray-600">₹{month.team_bonus?.toLocaleString() || 0}</td>
                          <td className="text-right py-2 px-2 font-bold text-green-700 bg-green-50">
                            ₹{month.total_earned?.toLocaleString() || 0}
                          </td>
                          <td className="text-right py-2 px-2 text-green-600">₹{month.paid?.toLocaleString() || 0}</td>
                          <td className="text-right py-2 px-2 text-orange-600">₹{month.pending?.toLocaleString() || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-bold">
                        <td className="py-2 px-2">Total</td>
                        <td className="text-center py-2 px-2">
                          {historyData.reduce((sum, m) => sum + (m.admissions || 0), 0)}
                        </td>
                        <td className="text-right py-2 px-2">₹{historyData.reduce((sum, m) => sum + (m.base_commission || 0), 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-2">₹{historyData.reduce((sum, m) => sum + (m.weekly_bonus || 0), 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-2">₹{historyData.reduce((sum, m) => sum + (m.monthly_bonus || 0), 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-2">₹{historyData.reduce((sum, m) => sum + (m.team_bonus || 0), 0).toLocaleString()}</td>
                        <td className="text-right py-2 px-2 text-green-700 bg-green-100">
                          ₹{historyData.reduce((sum, m) => sum + (m.total_earned || 0), 0).toLocaleString()}
                        </td>
                        <td className="text-right py-2 px-2 text-green-600">
                          ₹{historyData.reduce((sum, m) => sum + (m.paid || 0), 0).toLocaleString()}
                        </td>
                        <td className="text-right py-2 px-2 text-orange-600">
                          ₹{historyData.reduce((sum, m) => sum + (m.pending || 0), 0).toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Summary Cards */}
              {historyData.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t">
                  <div className="bg-indigo-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-indigo-600">Total Admissions</p>
                    <p className="text-xl font-bold text-indigo-700">
                      {historyData.reduce((sum, m) => sum + (m.admissions || 0), 0)}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-green-600">Total Earned</p>
                    <p className="text-xl font-bold text-green-700">
                      ₹{historyData.reduce((sum, m) => sum + (m.total_earned || 0), 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-blue-600">Total Paid</p>
                    <p className="text-xl font-bold text-blue-700">
                      ₹{historyData.reduce((sum, m) => sum + (m.paid || 0), 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-orange-600">Total Pending</p>
                    <p className="text-xl font-bold text-orange-700">
                      ₹{historyData.reduce((sum, m) => sum + (m.pending || 0), 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
